<script setup lang="ts">
    import { computed, nextTick, ref, toRaw, watch } from 'vue';
    import saveAs from 'file-saver';
    import { AgGridVue } from 'ag-grid-vue3';
    import {
        themeQuartz,
        type ColDef,
        type ColumnResizedEvent,
        type ColumnState,
        type ColumnVisibleEvent,
        type GetMainMenuItems,
        type GetRowIdParams,
        type GridApi,
        type GridReadyEvent,
        type ModelUpdatedEvent,
        type SetFilterHandler,
        type SideBarDef,
        type StatusPanelDef,
        type ValueGetterParams
    } from 'ag-grid-community';
    import { useAgGridEnterprise } from '@/shared/helpers/agGrid';
    import { http } from '@/shared/httpWrapper';
    import useShow from '@/shared/composables/useShow';
    import { getAttachment, getAttachmentName, type CodeSetViewResultCodeRow } from '@/shared/helpers/api';
    import { getNameLabel } from '@/shared/helpers/nameWithAliasHelper';
    import type { CodeDef } from '@/shared/helpers/dataHelpers';
    import CodeCellRenderer from './CodeCellRenderer.vue';
    import CodeEditCellRenderer from './CodeEditCellRenderer.vue';
    import { defaultFilterModel, getFilterOptions, isCaseSensitiveColumn, toClqlQueries, type FilterModel } from './CodeGridFilters';
    import {
        decodeFilterModel,
        decodeRowGroups,
        decodeSort,
        encodeFilterModel,
        encodeRowGroups,
        encodeSort,
        hasGridUrlState,
        type GridSort
    } from './CodeGridUrlState';
    import {
        buildReferenceLabels,
        columnStateStorageKey,
        compareFilterDate,
        filterStateStorageKey,
        getCellAlignmentClass,
        getCellText,
        getCellValue,
        getColumnFilter,
        getColumnWidth,
        getStoredVisibleColumns,
        isHiddenByDefault,
        isNumericColumn,
        columnWidthStorageKey,
        sortColumnStorageKey,
        sortDirectionStorageKey
    } from './CodeGrid.helper';

    useAgGridEnterprise();

    const svgRowHeight = 100;
    const defaultRowHeight = 26;

    const props = withDefaults(defineProps<{
        rows: CodeSetViewResultCodeRow[];
        columns: CodeDef[];
        libraryName: string;
        nameAlias?: string;
        editButtonEnabled?: boolean;
        urlState?: { filter?: string; group?: string; sort?: string };
    }>(), {
        nameAlias: '',
        editButtonEnabled: false,
        urlState: undefined
    });

    const emit = defineEmits<{
        edit: [row: CodeSetViewResultCodeRow];
        'visible-columns': [fields: string[]];
        'displayed-count': [count: number];
        filters: [queries: string[][]];
        'url-state': [state: { filter?: string; group?: string; sort?: string }];
    }>();

    const { showError } = useShow();
    const gridApi = ref<GridApi<CodeSetViewResultCodeRow> | null>(null);

    const libraryName = computed(() => props.libraryName);
    const hasSvgColumn = computed(() => props.columns.some(c => c.type === 'Svg'));
    const rowHeight = computed(() => hasSvgColumn.value ? svgRowHeight : defaultRowHeight);

    // Kept out of the column definitions so that filtering does not recalculate widths.
    const columnWidths = ref<Record<string, number>>({});
    // Maps each reference column's stored identity to the text currently displayed for it.
    const referenceLabels = computed(() => buildReferenceLabels(props.columns, props.rows));

    watch(() => props.columns, (columns) => {
        columnWidths.value = Object.fromEntries(columns.map(col => [col.field, getColumnWidth(props.rows, col, getHeaderName(col))]));
    }, { immediate: true });

    function getHeaderName(col: CodeDef): string {
        if (col.label === 'Name')
            return getNameLabel(props.nameAlias);

        // An attribute is not required to carry a label.
        return col.label ?? '';
    }

    // The reference display mode is read inside the value getters, which AG Grid runs outside
    // of Vue's reactivity, so changing it has to refresh the cells and the filter values, and
    // regroup and re-sort the rows, which are grouped and sorted by the displayed text.
    const referenceDisplayModes = computed(() => props.columns
        .filter(col => col.type.toUpperCase() === 'CODEREF')
        .map(col => `${col.field}:${col.referenceDisplayMode}`)
        .join(','));

    watch(referenceDisplayModes, () => {
        if (!gridApi.value)
            return;

        const codeRefFields = props.columns.filter(col => col.type.toUpperCase() === 'CODEREF').map(col => col.field);
        for (const field of codeRefFields) {
            // On first load the columns arrive before the grid has them, and their filters start out current.
            if (gridApi.value.getColumn(field))
                gridApi.value.getColumnFilterHandler<SetFilterHandler>(field)?.refreshFilterValues();
        }

        gridApi.value.refreshClientSideRowModel('group');
        gridApi.value.refreshCells({ force: true });
    });

    const theme = themeQuartz.withParams({
        spacing: 4,
        fontFamily: 'inherit',
        oddRowBackgroundColor: '#fafafa',
        borderColor: '#dbdbdb',
        wrapperBorder: false
    });

    const defaultColDef: ColDef<CodeSetViewResultCodeRow> = {
        sortable: true,
        resizable: true,
        minWidth: 90,
        filter: true,
        filterParams: { buttons: ['reset'] },
        enableRowGroup: true,
        enableValue: true,
        tooltipValueGetter: params => params.valueFormatted ?? params.value?.toString() ?? null
    };

    // Column visibility is handled by the grid, so it offers both tool panels.
    const sideBar: SideBarDef = {
        toolPanels: [
            {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                    suppressPivotMode: true,
                    suppressValues: true
                }
            },
            {
                id: 'filters',
                labelDefault: 'Filters',
                labelKey: 'filters',
                iconKey: 'filter',
                toolPanel: 'agFiltersToolPanel'
            }
        ],
        defaultToolPanel: undefined
    };

    // Column visibility is handled by the grid, so the column chooser is offered here too.
    const getMainMenuItems: GetMainMenuItems<CodeSetViewResultCodeRow> = () => {
        return [
            'sortAscending',
            'sortDescending',
            'sortUnSort',
            'separator',
            'pinSubMenu',
            'separator',
            'autoSizeThis',
            'autoSizeAll',
            'separator',
            'rowGroup',
            'rowUnGroup',
            'separator',
            'columnChooser',
            'resetColumns'
        ];
    };

    // The item count lives in the page header, so the grid's own count would only repeat it.
    const statusBar: { statusPanels: StatusPanelDef[] } = {
        statusPanels: [
            { statusPanel: 'agSelectedRowCountComponent', align: 'left' },
            { statusPanel: 'agAggregationComponent', align: 'right' }
        ]
    };

    // The grid can be mounted before the column definitions have been built, and the column
    // definitions are rebuilt whenever the columns change. State is therefore restored once, and
    // only once there are columns to restore it onto.
    const stateInitialised = ref(false);

    const columnDefs = computed<ColDef<CodeSetViewResultCodeRow>[]>(() => {
        const defs: ColDef<CodeSetViewResultCodeRow>[] = [];

        if (props.editButtonEnabled) {
            defs.push({
                colId: 'edit',
                headerName: '',
                width: 36,
                minWidth: 36,
                maxWidth: 36,
                sortable: false,
                resizable: false,
                filter: false,
                suppressMovable: true,
                suppressHeaderMenuButton: true,
                pinned: 'left',
                lockPosition: true,
                cellClass: 'edit-cell',
                cellRenderer: CodeEditCellRenderer,
                cellRendererParams: { onEdit: (row: CodeSetViewResultCodeRow) => emit('edit', row) }
            });
        }

        return defs.concat(props.columns.map(createColumnDef));
    });

    watch(columnDefs, async () => {
        await nextTick();

        if (!stateInitialised.value) {
            initialiseState();
            return;
        }

        restoreColumnState();
    });

    function createColumnDef(col: CodeDef): ColDef<CodeSetViewResultCodeRow> {
        const isSvg = col.type === 'Svg';
        const filter = getColumnFilter(col);
        const def: ColDef<CodeSetViewResultCodeRow> = {
            colId: col.field,
            headerName: getHeaderName(col),
            headerTooltip: col.description ?? undefined,
            valueGetter: (params: ValueGetterParams<CodeSetViewResultCodeRow>) => params.data ? getCellValue(params.data, col) : null,
            cellClass: [getCellAlignmentClass(col.type), 'keep-spaces'].filter(c => !!c) as string[],
            cellRenderer: usesCellRenderer(col) ? CodeCellRenderer : undefined,
            // toRaw keeps the reactive CodeDef out of the column definitions. ag-grid-vue3 deep
            // watches them, so a reactive column here would rebuild the grid (resetting column
            // visibility to the defaults) whenever the reference display mode changes.
            cellRendererParams: { col: toRaw(col), onDownloadAttachment: downloadAttachment },
            // Numbers are sorted numerically, but displayed exactly as they are stored.
            valueFormatter: isNumericColumn(col) ? params => getCellText(col, params.data) : undefined,
            sortable: !isSvg,
            filter,
            filterParams: getFilterParams(filter, col),
            enableRowGroup: !isSvg,
            tooltipValueGetter: isSvg ? () => null : undefined,
            // The default visibility lives on the column definition so that "Reset columns"
            // restores it. The user's own choice is kept in the saved column state.
            hide: isHiddenByDefault(col.field),
            width: columnWidths.value[col.field] ?? 150
        };

        return def;
    }

    // The distinct non-blank values per column, used to send the shorter of "these values" or
    // "not those values" so that deselecting a few entries does not build a huge query.
    const distinctValues = computed(() => {
        const values = new Map<string, string[]>();
        for (const col of props.columns) {
            if (getColumnFilter(col) !== 'agSetColumnFilter')
                continue;

            const seen = new Set<string>();
            for (const row of props.rows) {
                const value = getCellText(col, row);
                if (value !== '')
                    seen.add(value);
            }
            values.set(col.field, [...seen]);
        }
        return values;
    });

    function getFilterParams(filter: string | boolean, col: CodeDef) {
        const filterOptions = getFilterOptions(typeof filter === 'string' ? filter : '');
        const base: Record<string, unknown> = { buttons: ['reset'], maxNumConditions: 1 };

        if (filterOptions)
            base.filterOptions = filterOptions;

        // AG Grid leaves out both ends of a range by default, while the range is translated with
        // both ends included, so the grid is told to include them too.
        if (filter === 'agNumberColumnFilter' || filter === 'agDateColumnFilter')
            base.inRangeInclusive = true;

        if (filter === 'agDateColumnFilter')
            base.comparator = compareFilterDate;

        if (filter === 'agSetColumnFilter') {
            base.excelMode = 'windows';
            if (isCaseSensitiveColumn(col.field))
                base.caseSensitive = true;
            if (col.type.toUpperCase() === 'CODEREF') {
                // Filter on the stored identity so that the query sent to the API does not depend
                // on how the reference happens to be displayed, but show the formatted text.
                base.keyCreator = (params: { data?: CodeSetViewResultCodeRow }) => getCellText(col, params.data);
                base.valueFormatter = (params: { value: string }) => referenceLabels.value.get(col.field)?.get(params.value) ?? params.value;
            }
        }

        return base;
    }

    function usesCellRenderer(col: CodeDef): boolean {
        if (col.field === 'name' || col.field === 'attachmentKey' || col.type === 'Svg')
            return true;

        return ['BOOL', 'CODEREF', 'LIBRARYREF', 'URI'].includes(col.type.toUpperCase());
    }

    function getRowId(params: GetRowIdParams<CodeSetViewResultCodeRow>) {
        return params.data.id;
    }

    function onGridReady(event: GridReadyEvent<CodeSetViewResultCodeRow>) {
        gridApi.value = event.api;
        initialiseState();
    }

    // The grid can be mounted before the column definitions have been built, and the column
    // definitions are rebuilt whenever the columns change. State is therefore restored once, and
    // only once there are columns to restore it onto.
    function initialiseState() {
        if (!gridApi.value || stateInitialised.value || props.columns.length === 0)
            return;

        stateInitialised.value = true;

        restoreColumnState();
        restoreFilterState();
        applyUrlState();
    }

    // A link that carries a filter, grouping or sorting wins over what this browser had saved, so
    // that opening a shared link shows what the sender was looking at. The link describes the whole
    // view, so a dimension the link leaves out is cleared rather than left at the saved value:
    // otherwise a filter-only link would keep the recipient's own grouping, and a sort-only link
    // would keep their own filter, which changes the rows that export and publish would act on.
    function applyUrlState() {
        const urlState = props.urlState;
        if (!urlState || !gridApi.value)
            return;

        const hasUrlState = hasGridUrlState(urlState);
        if (!hasUrlState)
            return;

        const filterModel = decodeFilterModel(urlState.filter ?? null, props.columns, distinctValues.value) ?? {};
        gridApi.value.setFilterModel(filterModel);
        localStorage.setItem(filterStateStorageKey(libraryName.value), JSON.stringify(filterModel));

        const rowGroups = decodeRowGroups(urlState.group);
        const sorts = decodeSort(urlState.sort);
        const sortByColId = new Map(sorts.map((sort, index) => [sort.colId, { sort: sort.sort, sortIndex: index }]));

        gridApi.value.applyColumnState({
            state: gridApi.value.getColumnState().map(state => ({
                colId: state.colId,
                rowGroupIndex: rowGroups.indexOf(state.colId) === -1 ? null : rowGroups.indexOf(state.colId),
                sort: sortByColId.get(state.colId)?.sort ?? null,
                sortIndex: sortByColId.get(state.colId)?.sortIndex ?? null
            }))
        });

        // Persisted straight away so that a later rebuild of the column definitions, which restores
        // the saved state, cannot undo what the link asked for.
        saveColumnState();
    }

    function emitUrlState() {
        if (!gridApi.value)
            return;

        const columnState = gridApi.value.getColumnState();

        const rowGroups = columnState
            .filter(state => state.rowGroupIndex !== null && state.rowGroupIndex !== undefined)
            .sort((a, b) => a.rowGroupIndex! - b.rowGroupIndex!)
            .map(state => state.colId);

        const sorts: GridSort[] = columnState
            .filter(state => !!state.sort)
            .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
            .map(state => ({ colId: state.colId, sort: state.sort as 'asc' | 'desc' }));

        emit('url-state', {
            filter: encodeFilterModel(gridApi.value.getFilterModel() as FilterModel, distinctValues.value) ?? undefined,
            group: encodeRowGroups(rowGroups) ?? undefined,
            sort: encodeSort(sorts) ?? undefined
        });
    }

    function restoreFilterState() {
        const stored = localStorage.getItem(filterStateStorageKey(libraryName.value));
        if (stored) {
            try {
                const model = JSON.parse(stored) as FilterModel;

                // A stored filter outlives the code that wrote it, so it is only applied if it
                // still translates. One that does not would show a filtered grid while export and
                // publish acted on something else.
                toClqlQueries(model, props.columns, distinctValues.value);

                gridApi.value!.setFilterModel(model);
                return;
            } catch {
                localStorage.removeItem(filterStateStorageKey(libraryName.value));
            }
        }

        gridApi.value!.setFilterModel(defaultFilterModel);
    }

    function restoreColumnState() {
        if (!gridApi.value)
            return;

        const stored = localStorage.getItem(columnStateStorageKey(libraryName.value));
        if (stored) {
            try {
                const state = JSON.parse(stored) as ColumnState[];
                gridApi.value.applyColumnState({ state, applyOrder: true });
                emitVisibleColumns();
                return;
            } catch {
                localStorage.removeItem(columnStateStorageKey(libraryName.value));
            }
        }

        applyLegacyState();
        emitVisibleColumns();
    }

    // Picks up the visibility, sort and widths saved by the previous code list so that user settings survive.
    function applyLegacyState() {
        const state: ColumnState[] = [];
        const storedVisible = getStoredVisibleColumns(libraryName.value);

        for (const col of props.columns) {
            const item: ColumnState = { colId: col.field };
            let used = false;

            const width = Number(localStorage.getItem(columnWidthStorageKey(libraryName.value, col.field)));
            if (width > 0) {
                item.width = width;
                item.flex = null;
                used = true;
            }

            if (storedVisible) {
                item.hide = !storedVisible.includes(col.field.toLowerCase());
                used = true;
            }

            if (used)
                state.push(item);
        }

        const sortColId = localStorage.getItem(sortColumnStorageKey(libraryName.value));
        if (sortColId && gridApi.value!.getColumn(sortColId)) {
            const sort = localStorage.getItem(sortDirectionStorageKey(libraryName.value)) === 'desc' ? 'desc' : 'asc';
            const existing = state.find(s => s.colId === sortColId);
            if (existing)
                existing.sort = sort;
            else
                state.push({ colId: sortColId, sort });
        }

        if (state.length)
            gridApi.value!.applyColumnState({ state });
    }

    function saveColumnState() {
        if (!gridApi.value)
            return;

        localStorage.setItem(columnStateStorageKey(libraryName.value), JSON.stringify(gridApi.value.getColumnState()));
        emitUrlState();
    }

    function emitVisibleColumns() {
        if (!gridApi.value)
            return;

        const visible = gridApi.value.getColumnState()
            // A column used for row grouping still belongs in the export and the lookup URL.
            .filter(state => (!state.hide || state.rowGroupIndex !== null) && state.colId !== 'edit')
            .map(state => state.colId);

        emit('visible-columns', visible);
    }

    function onColumnVisible(event: ColumnVisibleEvent<CodeSetViewResultCodeRow>) {
        // A grid rebuild re-applies the default visibility from the column definitions, which
        // must not be mistaken for the user changing which columns they want to see.
        if (event.source === 'gridOptionsChanged')
            return;

        saveColumnState();
        emitVisibleColumns();
    }

    // Grouping by a hidden column, or ungrouping one, changes which columns are exported without
    // necessarily changing visibility, so the column visible event alone does not cover it.
    function onColumnRowGroupChanged() {
        saveColumnState();
        emitVisibleColumns();
    }

    function onColumnResized(event: ColumnResizedEvent<CodeSetViewResultCodeRow>) {
        if (event.finished)
            saveColumnState();
    }

    // The count of real codes left after the grid's own filters. Group rows are skipped so that
    // row grouping does not change the number.
    function emitDisplayedCount(event: ModelUpdatedEvent<CodeSetViewResultCodeRow>) {
        let count = 0;
        event.api.forEachNodeAfterFilter((node) => {
            if (!node.group)
                count++;
        });

        emit('displayed-count', count);
    }

    // Export, publish and the lookup URL are all driven by the grid's filters, translated into
    // the query format the API understands.
    /**
     * Translation comes first, and the filter is only saved once it has succeeded.
     *
     * The page learns the filter from this message alone, and an empty list means "no filter" to
     * the API rather than "no rows". So anything thrown in between used to leave export and publish
     * acting on the whole code set while the grid showed a subset — and because the model had
     * already been written to storage, the broken state came back on every later visit.
     */
    function emitFilters() {
        if (!gridApi.value)
            return;

        const model = gridApi.value.getFilterModel() as FilterModel;

        let queries: string[][];
        try {
            queries = toClqlQueries(model, props.columns, distinctValues.value);
        } catch {
            // Leaving the stored filter untouched keeps the last one that did translate, rather
            // than persisting one that cannot be sent anywhere.
            showError('This filter could not be applied. Use "Reset columns" to start again.');
            return;
        }

        localStorage.setItem(filterStateStorageKey(libraryName.value), JSON.stringify(model));
        emit('filters', queries);
        emitUrlState();
    }

    // A set filter may be sent as the values left out, or as "has a value", and both stand for the
    // selection only against the values the code set held when it was translated. The grid does not
    // raise a filter change when new rows bring a value nobody selected; it just hides those rows.
    // So the filter is translated again whenever the values change, or export and publish would act
    // on the new rows the grid is hiding.
    watch(distinctValues, () => {
        if (stateInitialised.value)
            emitFilters();
    });

    function clearFilters() {
        gridApi.value?.setFilterModel(null);
    }

    function autoSizeColumns() {
        gridApi.value?.autoSizeAllColumns();
    }

    function resetColumns() {
        localStorage.removeItem(columnStateStorageKey(libraryName.value));
        localStorage.removeItem(filterStateStorageKey(libraryName.value));
        gridApi.value?.resetColumnState();
        gridApi.value?.setFilterModel(defaultFilterModel);
        saveColumnState();
        emitVisibleColumns();
    }

    defineExpose({ clearFilters, autoSizeColumns, resetColumns });

    async function downloadAttachment(key: string) {
        try {
            const [file, fileName] = await Promise.all([getAttachment(http, key), getAttachmentName(http, key)]);
            saveAs(file, fileName);
        } catch {
            showError('Could not download attachment');
        }
    }
</script>

<template>
    <div
        class="code-grid">
        <ag-grid-vue
            :row-data="rows"
            :column-defs="columnDefs"
            :default-col-def="defaultColDef"
            :theme="theme"
            :row-height="rowHeight"
            :get-row-id="getRowId"
            :side-bar="sideBar"
            :status-bar="statusBar"
            :get-main-menu-items="getMainMenuItems"
            :row-group-panel-show="'onlyWhenGrouping'"
            :group-display-type="'groupRows'"
            :suppress-group-changes-column-visibility="true"
            :cell-selection="true"
            :enable-browser-tooltips="true"
            :animate-rows="false"
            :multi-sort-key="'ctrl'"
            style="height: 100%; width: 100%;"
            @grid-ready="onGridReady"
            @model-updated="emitDisplayedCount"
            @filter-changed="emitFilters"
            @sort-changed="saveColumnState"
            @column-moved="saveColumnState"
            @column-pinned="saveColumnState"
            @column-visible="onColumnVisible"
            @column-row-group-changed="onColumnRowGroupChanged"
            @column-resized="onColumnResized" />
    </div>
</template>

<style scoped>
.code-grid {
    height: 100%;
    min-height: 35rem;
    width: 100%;
}

.code-grid :deep(.keep-spaces) {
    white-space: pre;
}

/* Digits of equal width so that numbers line up down the column. */
.code-grid :deep(.has-text-right) {
    font-variant-numeric: tabular-nums;
}

/*
 * The edit cell's button, styled from here rather than from the renderer's own scoped block: AG
 * Grid instantiates cell renderers outside the normal component tree, so they never receive the
 * `data-v-` attribute that scoped styles match on. The button fills the cell so that the pointer,
 * the click target and the keyboard focus ring cover all of it rather than just the small icon,
 * which is why the cell's own padding is removed too.
 */
.code-grid :deep(.ag-cell.edit-cell) {
    padding-left: 0;
    padding-right: 0;
}

.code-grid :deep(.edit-cell-button) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    color: inherit;
    font: inherit;
    cursor: pointer;
}

/* Shown only when the button is reached by keyboard, so the pointer sees the pencil alone. */
.code-grid :deep(.edit-cell-button:focus-visible) {
    outline: 2px solid #3273dc;
    outline-offset: -2px;
}
</style>
